import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Observable } from 'rxjs';
import { ActivityTypeService } from 'src/app/shared/store/activity-type/activity-type.service';
import { ActivityTypeModel } from 'src/app/shared/store/activity-type/types';
import { SubHeaderService } from 'src/app/shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { View } from '../../../../shared/store/view/types';
import { ViewService } from '../../../../shared/store/view/views.service';
import { take } from 'rxjs/operators';
import { FormService } from '../../../../shared/store/form/form.service';

@UntilDestroy()
@Component({
    selector: 'app-activity-type-edit',
    templateUrl: './activity-type-edit.component.html',
    styleUrls: ['./activity-type-edit.component.scss'],
    standalone: false
})
export class ActivityTypeEditComponent implements OnInit, OnDestroy {
  activityType$: Observable<ActivityTypeModel>;
  activityTypeForm: FormGroup;
  view$: Observable<View>;

  constructor(
    private readonly activityTypeService: ActivityTypeService,
    private readonly subHeaderService: SubHeaderService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formService: FormService,
    private readonly formBuilder: FormBuilder
  ) {
    this.activityType$ = this.activityTypeService.getById.value$;
    this.view$ = this.viewService.getViews.value$;
  }

  ngOnInit(): void {
    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'activityTypeUpdate') this.onSubmit();
        if (x == 'activityTypeDetails') {
          this.formService.clear();
          this.router.navigate([
            'Crm/ActivityTypes/Details/',
            this.route.snapshot.params['id'],
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.activityTypeService.getById.call(this.route.snapshot.params['id']);

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x)
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
      });

    this.activityTypeForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(80)]],
      description: ['', [Validators.maxLength(255)]],
      delay: [0, [Validators.required]],
      delayUnit: [0, [Validators.required]],
      delayFrom: [0, [Validators.required]],
      entityType: ['', [Validators.maxLength(50)]],
      nextActivityPolicy: [0, [Validators.required]],
      nextActivityTypeId: [undefined],
      defaultUserId: [undefined],
    });

    this.activityType$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x)
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@activityTypeEdit', x.name);
        this.activityTypeForm.patchValue({
          name: x.name,
          description: x.description,
          delay: x.delay,
          delayUnit: x.delayUnit,
          delayFrom: x.delayFrom,
          entityType: x.entityType,
          nextActivityPolicy: x.nextActivityPolicy,
          nextActivityTypeId: x.nextActivityTypeId,
          defaultUserId: x.defaultUserId,
        });
      });
  }

  onSubmit(): void {
    if (this.activityTypeForm.invalid) {
      this.activityTypeForm.markAllAsTouched();
      this.formService.countErrors(this.activityTypeForm, true);
      return;
    }

    this.formService.clear();

    let result$ = this.activityTypeService.update.call({
      id: this.route.snapshot.params['id'],
      name: this.activityTypeForm.value.name!,
      description: this.activityTypeForm.value.description!,
      delay: this.activityTypeForm.value.delay!,
      delayUnit: this.activityTypeForm.value.delayUnit!,
      delayFrom: this.activityTypeForm.value.delayFrom!,
      entityType: this.activityTypeForm.value.entityType!,
      nextActivityPolicy: this.activityTypeForm.value.nextActivityPolicy!,
      nextActivityTypeId: this.activityTypeForm.value.nextActivityTypeId!,
      defaultUserId: this.activityTypeForm.value.defaultUserId!,
    });
    result$
      .pipe(
        filter((x) => !!x),
        take(1)
      )
      .subscribe((result) => {
        this.router.navigate(['/Crm/ActivityTypes/Details', result.id]);
      });
  }

  ngOnDestroy(): void {
    this.activityTypeService.update.reset();
    this.breadcrumbService.set('@activityTypeEdit', ' ');
  }
}
